/**
 * Shareable join links.
 *
 * `/join/{eventCode}` is the canonical form. `?join={eventCode}` is accepted as
 * a fallback so links still resolve on static hosting that has no SPA rewrite
 * rule configured yet.
 *
 * Invite origin: optional `REACT_APP_INVITE_BASE_URL` (a valid http(s) origin)
 * wins when set, otherwise `window.location.origin`. Nothing here hard-codes a
 * LAN IP; developers who need a phone-reachable QR set the env var locally.
 */
import { normalizeEventCode } from './eventCode';

export const JOIN_ROUTE_PREFIX = '/join';

export function sanitizeInviteOrigin(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

export function configuredInviteOrigin() {
  // Bracket access so tests can set the var at runtime; CRA inlines dot access.
  return sanitizeInviteOrigin(process.env['REACT_APP_INVITE_BASE_URL']);
}

export function resolveInviteOrigin(explicitOrigin) {
  const fromArg = sanitizeInviteOrigin(explicitOrigin);
  if (fromArg) return fromArg;

  const fromEnv = configuredInviteOrigin();
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return '';
}

export function isLocalhostOrigin(origin) {
  const sanitized = sanitizeInviteOrigin(origin) || origin;
  if (!sanitized) return false;

  try {
    const host = new URL(sanitized).hostname.toLowerCase();
    return host === 'localhost'
      || host === '127.0.0.1'
      || host === '[::1]'
      || host === '::1'
      || host.endsWith('.localhost');
  } catch {
    return false;
  }
}

export function buildJoinPath(eventCode) {
  const code = normalizeEventCode(eventCode);
  return code ? `${JOIN_ROUTE_PREFIX}/${code}` : JOIN_ROUTE_PREFIX;
}

export function buildJoinUrl(eventCode, origin) {
  const base = resolveInviteOrigin(origin);
  return `${String(base).replace(/\/$/, '')}${buildJoinPath(eventCode)}`;
}

export function parseJoinCodeFromPath(pathname) {
  if (typeof pathname !== 'string') return '';
  const match = pathname.match(/^\/join\/([^/?#]+)/i);
  return match ? normalizeEventCode(decodeURIComponent(match[1])) : '';
}

export function parseJoinCodeFromSearch(search) {
  if (typeof search !== 'string' || !search) return '';
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    return normalizeEventCode(params.get('join') || '');
  } catch {
    return '';
  }
}

/** Resolve a join code from a full location object, path first. */
export function resolveJoinCode(location) {
  return parseJoinCodeFromPath(location?.pathname)
    || parseJoinCodeFromSearch(location?.search);
}
